import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  MetaGoalBucket,
  PortalComparisonMode,
  PortalGrouping,
} from '../../../../types/portalReport';
import type { ReportingDatePreset } from '../../../../types/reporting';
import { presetToRange, type DateRange } from '../../../../lib/reportingDateRange';
import { nextLocationParams } from '../../../../lib/portalReport/locationSelection';
import { resolveDefaultPortalPreset } from '../../../../lib/portalReport/reportPeriod';
import {
  ACCOUNT_PARAM,
  ACCOUNTS_PARAM,
  COMPARISON_PARAM,
  customRangeParams,
  FROM_PARAM,
  GROUPING_PARAM,
  parseComparison,
  parseGrouping,
  parsePreset,
  PRESET_PARAM,
  TO_PARAM,
} from '../../../../lib/portalReport/reportUrlState';
import { PORTAL_PRESETS } from './PeriodControl';
import type { ReportParams } from './reportApi';

// CLIENT PORTAL Phase 1 — the report's shared page state (period, grouping,
// campaign filter, location drill-down), so the three pages behave identically.
//
// ⚠️⚠️ ALMOST EVERY CONTROL LIVES IN THE URL (§7, 2026-09-03). It used to be the
// selected account alone, and that asymmetry was a live defect rather than a
// tidiness question: this hook is called ONCE PER ROUTE COMPONENT, so a change
// of channel tab mounted a FRESH copy and the period, comparison and grouping
// all reset to their defaults. Scott reported it twice — as the club falling
// back to All locations, and separately as the date "defaulting back to Last
// month" — and the two are one root cause. He noticed the club first because it
// is the only one of them the page NAMES.
//
// ⚠️ The account's own reason generalises to all of them: the portal's left nav
// lists one link per location and component state is not something a link can
// set — and a PERIOD and a COMPARISON are exactly as forwardable. "Look at
// August vs last year for Kalgoorlie" should be one link. It also survives a
// refresh and the back button, which hoisting the hook would not.
//
// ⚠️⚠️ `campaignIds` IS THE ONE THAT STAYS LOCAL, AND THAT IS THE FIX FOR THE
// SECOND TRAP RATHER THAN AN OMISSION. Meta and Google campaign ids are
// different id spaces, so a Meta filter carried to the Google tab yields a
// SILENTLY EMPTY report. Because the filter is not in the URL there is nothing
// to carry — a route change resets it by construction, so "it must not survive a
// channel change" is structural rather than a rule each navigation site has to
// remember. `carryAcrossTabChange`'s allowlist is the belt to that braces.
//
// ⚠️ `null` — no `?account=` at all — is the WHOLE SCOPE, and that is a real
// destination rather than a missing value: for a single-location client it is
// their club, and for a GROUP client it is the group view the group report
// template will eventually render. Defaulting it to the first account would drop
// a group-scoped client into one club's report and call it theirs.
//
// ⚠️ `replace: true` on the write, so drilling into a club and back does not fill
// the client's history with report states — the nav is how they move, and the
// back button should leave the report, not step through it.

// ⚠️ THE PARAMETER NAMES LIVE IN THE PURE MODULE, so `carryAcrossTabChange`
// and this hook cannot disagree about what a parameter is called — a mismatch
// there would drop a control on every tab change and look like the §7 bug
// returning. Re-exported because callers already import them from here.
export {
  ACCOUNT_PARAM,
  ACCOUNTS_PARAM,
  COMPARISON_PARAM,
  FROM_PARAM,
  GROUPING_PARAM,
  PRESET_PARAM,
  TO_PARAM,
} from '../../../../lib/portalReport/reportUrlState';

/** YYYY-MM-DD in the CLIENT's local calendar — see ReportParams.today. */
export function todayLocal(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface ReportState {
  preset: ReportingDatePreset;
  setPreset: (p: ReportingDatePreset) => void;
  custom: Partial<DateRange>;
  setCustom: (r: Partial<DateRange>) => void;
  grouping: PortalGrouping;
  setGrouping: (g: PortalGrouping) => void;
  campaignIds: string[] | undefined;
  setCampaignIds: (ids: string[] | undefined) => void;
  /**
   * The goal-split Meta page's campaign-goal filter. ⚠️ LOCAL STATE for the same
   * reason as `campaignIds`: it is Meta-only, so it must not survive a tab change,
   * and not being in the URL makes that structural.
   */
  goalBucket: MetaGoalBucket | undefined;
  setGoalBucket: (b: MetaGoalBucket | undefined) => void;
  comparison: PortalComparisonMode;
  setComparison: (mode: PortalComparisonMode) => void;
  accountId: string | null;
  setAccountId: (id: string | null) => void;
  /**
   * The picker's selection (§9). ⚠️ `null` = no filter (the whole scope); `[]` =
   * filtered to nothing. Two different states, all the way to the server.
   */
  accountIds: string[] | null;
  setAccountIds: (ids: string[] | null) => void;
  /**
   * ⚠️⚠️ THE REPORT WAS REACHED BY DRILLING INTO ONE CLUB FROM THE NAV.
   *
   * Named for the MODE rather than for the widget it governs, because it is a
   * fact about how this page was reached and the picker is only its first
   * consumer.
   *
   * ⚠️⚠️ IT IS THE PRESENCE OF THE `account` PARAMETER, DELIBERATELY — NOT
   * `PortalNavSelection`. That type is lossy about WHICH parameter produced it:
   * a single-club selection made THROUGH the picker writes `?accounts=<id>`,
   * the server narrows to that club and reports a `selectedAccountId`, so it
   * resolves to `kind: 'account'` too. Gate the picker on that and it
   * disappears the moment somebody picks one club with it — trapping them on
   * one club with no control to get back.
   */
  navDrillDown: boolean;
  /** Null until a custom range has BOTH ends — see the comment below. */
  params: ReportParams | null;
}

export function useReportState(): ReportState {
  // ⚠️⚠️ THIS MONTH ON OPEN, ONCE IT HOLDS THREE COMPLETE DAYS — Scott, 2026-09-02.
  //
  // ⚠️ THE PREVIOUS DEFAULT WAS `last_month`, AND ITS REASON IS KEPT ON RECORD
  // RATHER THAN DELETED: "the natural question a client opens a report to ask,
  // and the only default that is a COMPLETE window — a month-to-date default
  // would greet them with a partial figure every single time." That reasoning
  // still holds; Scott OVERRULED it knowingly, because a client checking in
  // during the month wants the month they are in. A default whose reason is
  // deleted looks like it never had one.
  //
  // ⚠️ THE CONSEQUENCE THAT REASON WARNED ABOUT IS REAL AND IS NOW VISIBLE: this
  // month is a PARTIAL window, and `periodNote` says so on every render ("Today
  // is included and is still running, so its figures are incomplete"). What the
  // three-complete-day rule fixes is the harder edge — on the 1st the range is
  // one day consisting only of today, which holds no data until UTC midnight, so
  // the report would open all dashes. See `resolveDefaultPortalPreset`.
  //
  // ⚠️ A LAZY INITIALISER, so the clock is read once per mount rather than on
  // every render — and so a client who has picked a preset is never moved by the
  // date rolling over while their tab is open.
  // ⚠️ STILL A LAZY INITIALISER even though the preset now lives in the URL —
  // this is the value used when the parameter is ABSENT. Reading the clock on
  // every render would let the date rolling over while a tab sits open move a
  // reader's window underneath them.
  const [defaultPreset] = useState<ReportingDatePreset>(() =>
    resolveDefaultPortalPreset(new Date()),
  );
  // ⚠️⚠️ `campaignIds` IS THE ONLY CONTROL LEFT IN LOCAL STATE, deliberately.
  // See the header: not being in the URL is what makes "a Meta campaign filter
  // cannot reach the Google tab" structural.
  const [campaignIds, setCampaignIds] = useState<string[] | undefined>(undefined);
  const [goalBucket, setGoalBucket] = useState<MetaGoalBucket | undefined>(undefined);
  // ⚠️⚠️ PREVIOUS YEAR by default — Scott, 2026-09-02.
  //
  // ⚠️ THE PREVIOUS DEFAULT WAS `previous`, AND ITS REASON IS KEPT ON RECORD
  // RATHER THAN DELETED: "the like-for-like window, and the only one that always
  // exists. Previous year is the most likely to fall outside a client's
  // backfill, which would greet them with a no-comparison note." Overruled
  // knowingly: year-on-year is the comparison an owner actually asks for, and
  // seasonality makes previous-period misleading for a gym or a dealership.
  //
  // ⚠️⚠️ THE CONSEQUENCE IS REAL AND WILL BE SEEN. Meta backfill is TIERED and
  // plenty of accounts hold only 90 days, so those clients WILL open the report
  // to "The comparison period is only partly covered by our reporting, so a
  // change would be misleading." That is `comparisonUnavailableReason` behaving
  // exactly as designed — refusing to compute a percentage against half a window
  // — and it is NOT a bug to work around by quietly falling back to `previous`.
  // A silent basis change would be worse than the sentence: two clients reading
  // "vs last year" over different windows is the credibility problem the shared
  // `comparisonRange` exists to prevent.
  const [searchParams, setSearchParams] = useSearchParams();
  const accountId = searchParams.get(ACCOUNT_PARAM);

  // ⚠️ EVERY URL READ IS VALIDATED against its own vocabulary — a URL is
  // user-editable and forwardable, and a stale bookmark naming a renamed preset
  // must fall back to the default rather than reach the server or render an
  // empty control. The parsers are pure and live next door.
  const preset = parsePreset(searchParams.get(PRESET_PARAM), PORTAL_PRESETS, defaultPreset);
  const comparison = parseComparison(searchParams.get(COMPARISON_PARAM));
  const grouping = parseGrouping(searchParams.get(GROUPING_PARAM));
  const customFrom = searchParams.get(FROM_PARAM);
  const customTo = searchParams.get(TO_PARAM);
  const custom = useMemo<Partial<DateRange>>(
    // ⚠️ An END is OMITTED until it exists rather than written empty — the
    // calendar decides "extend" vs "start over" by whether it already has one,
    // so a placeholder end makes the second click restart the selection and the
    // reader can never finish a range.
    () => ({
      ...(customFrom ? { from: customFrom } : {}),
      ...(customTo ? { to: customTo } : {}),
    }),
    [customFrom, customTo],
  );

  /**
   * ⚠️ ONE WRITER for every URL-held control, so `replace: true` and the
   * merge-onto-previous behaviour are stated once. `replace` because the nav is
   * how a client moves through the report — the back button should leave it
   * rather than step through every control change.
   */
  const writeParams = (patch: Record<string, string | null>): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null) next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  };

  /**
   * The locations the picker has selected (§9, 2026-09-03).
   *
   * ⚠️⚠️ NULL AND EMPTY ARE DIFFERENT, all the way to the server. `null` — the
   * parameter absent — is "no filter, the whole scope"; `[]` — the parameter
   * present and empty — is "filtered to nothing", and the report reads nothing.
   * Collapsing them would answer a request for nothing with everything the
   * client holds.
   *
   * ⚠️ IN THE URL, like `?account=`, so a selection is bookmarkable and survives
   * a refresh — and so the left nav's per-club links keep working alongside it.
   */
  const accountIdsParam = searchParams.get(ACCOUNTS_PARAM);
  const accountIds = useMemo(
    () =>
      accountIdsParam === null
        ? null
        : accountIdsParam
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
    [accountIdsParam],
  );
  const setAccountIds = (ids: string[] | null): void => {
    // ⚠️ THE RULE IS PURE AND LIVES NEXT DOOR (`nextLocationParams`): an empty
    // selection still WRITES the parameter, and it clears the single-id
    // drill-down. Both are testable without a router that way.
    setSearchParams(
      (prev) => nextLocationParams(prev, ids, { many: ACCOUNTS_PARAM, one: ACCOUNT_PARAM }),
      { replace: true },
    );
  };
  const setAccountId = (id: string | null): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set(ACCOUNT_PARAM, id);
        else next.delete(ACCOUNT_PARAM);
        return next;
      },
      { replace: true },
    );
  };

  const today = todayLocal();

  const params = useMemo<ReportParams | null>(() => {
    if (preset === 'custom') {
      // ⚠️ HELD IN LOCAL STATE UNTIL BOTH ENDS EXIST, and not echoed back into
      // the picker. The calendar decides "extend the range" vs "start over" by
      // whether it already has an END, so feeding it a placeholder end makes the
      // second click restart the selection and the user can never finish a
      // range. It also means the page only refetches once a real range exists.
      if (!custom.from || !custom.to) return null;
      return {
        from: custom.from,
        to: custom.to,
        grouping,
        today,
        campaignIds,
        accountId,
        accountIds,
        comparison,
        goalBucket,
      };
    }
    const range = presetToRange(preset, new Date());
    return {
      from: range.from,
      to: range.to,
      grouping,
      today,
      campaignIds,
      accountId,
      accountIds,
      comparison,
      goalBucket,
    };
  }, [
    preset,
    custom.from,
    custom.to,
    grouping,
    today,
    campaignIds,
    accountId,
    accountIds,
    comparison,
    goalBucket,
  ]);

  return {
    preset,
    setPreset: (p) => {
      // ⚠️ LEAVING `custom` CLEARS THE RANGE, in the same write. Two writes would
      // give react-router an intermediate URL carrying a preset that is not
      // custom alongside a custom range, and `params` reads both.
      writeParams({
        [PRESET_PARAM]: p,
        ...(p === 'custom' ? {} : { [FROM_PARAM]: null, [TO_PARAM]: null }),
      });
    },
    custom,
    setCustom: (r) => {
      // ⚠️ An absent end DELETES its parameter rather than writing an empty
      // string — see `customRangeParams` for why a placeholder end traps the
      // calendar.
      const next = customRangeParams(r);
      writeParams({
        [FROM_PARAM]: next.from ?? null,
        [TO_PARAM]: next.to ?? null,
      });
    },
    grouping,
    setGrouping: (g) => writeParams({ [GROUPING_PARAM]: g }),
    campaignIds,
    setCampaignIds,
    goalBucket,
    setGoalBucket,
    comparison,
    setComparison: (mode) => writeParams({ [COMPARISON_PARAM]: mode }),
    accountId,
    setAccountId,
    // ⚠️ The PARAMETER's presence, read where the URL is owned rather than
    // re-derived by each consumer from a nullable id.
    navDrillDown: accountId !== null,
    accountIds,
    setAccountIds,
    params,
  };
}
